import { store } from 'redux/store';
import { State } from 'redux/initialState';
import { useTranslate } from 'hooks/useTranslate';
import { FC, createContext, useContext, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import errorSwal from 'utils/errorSwal';
import successSwal from 'utils/successSwal';

import { setModal } from 'modules/_shared/redux/modalActions';
import { addDocument } from 'modules/documents/redux/documentActions';
import { useGetLastOperationDate } from 'modules/transactions/hooks/useGetLastOperationDate';

import { cleanValue } from 'constants/formats';
import operationTypes from 'constants/operationTypes';
import fileTypes from 'constants/fileTypes';
import documentTypes from 'constants/documentTypes';
import cleanText from 'utils/cleanText';
import { addOperation } from 'modules/transactions/redux/operationActions';
import { getNewOperationDate } from 'utils/getNewOperationDate';
import { contributionModalInitValue } from '../constants/contributionModalInitValue';

import { ContributionModalContextType } from '../types/ContributionModalContextType';

import { useGetContributions } from '../hooks/useGetContributions';

const ContributionModalContext = createContext<ContributionModalContextType>(
  contributionModalInitValue
);

export const ContributionModalProvider: FC<{
  children: React.ReactNode; // eslint-disable-line
}> = ({ children }) => {
  const dispatch = useDispatch();
  const { t } = useTranslate();

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );
  const user = useSelector((state: State) => state.user);

  const [date, setDate] = useState<string>('');
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [scriptureView, setScriptureView] = useState<boolean>(true);
  const [comments, setComments] = useState<string>('');
  const [file, setFile] = useState<File | undefined>();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { lastOperationDate } = useGetLastOperationDate(
    actualSociety?._id
  ).data;

  const { data, isLoading: isLoadingContributions } = useGetContributions({
    societyId: actualSociety?._id,
    totalAmount,
    societyActualSharesCount: actualSociety?.sharesCount?.actual || 0,
    page: currentPage,
  });

  const handleCloseModal = () => {
    dispatch(setModal(null));
  };

  const handleChangeDate = (
    event: React.ChangeEvent<HTMLInputElement> // eslint-disable-line
  ) => {
    setDate(event.target.value);
  };

  const handleChangeTotalAmount = (
    event: React.ChangeEvent<HTMLInputElement> // eslint-disable-line
  ) => {
    setTotalAmount(cleanValue(event.target.value));
  };

  const handleChangeComments = (comment: string) => {
    setComments(comment);
  };

  const handleChangeFile = (file: File) => {
    setFile(file);
  };

  const handleChangePage = (page: number) => {
    setCurrentPage(page);
  };

  const handleChangeRowsPerPage = (rowsPerPage: number) => {
    setRowsPerPage(rowsPerPage);
  };

  const handleSaveContribution = async () => {
    if (scriptureView) {
      setScriptureView(false);
    } else {
      try {
        setIsLoading(true);

        let uploadedDocument = null;

        if (file) {
          const newDocument = {
            file,
            date: new Date(date),
            size: file.size,
            name: operationTypes.CONTRIBUTION_FILENAME,
            author: user['_id'],
            society: actualSociety['_id'],
            fileType: fileTypes[file.type as keyof typeof fileTypes],
            category: documentTypes.SCRIPTURES,
            description: t('DocumentLinkedToOperation'),
          };
          uploadedDocument = await store.dispatch(addDocument(newDocument));
        }

        const newOperationDate = getNewOperationDate(lastOperationDate, date);

        const operationData = {
          movements: [],
          transactions: [],
          contribution: { totalAmount },
          date: newOperationDate,
          nominalValue: actualSociety.nominalValue,
          society: actualSociety['_id'],
          user: user['_id'],
          documents: uploadedDocument?.['_id'] ? [uploadedDocument['_id']] : [],
          comments: cleanText(comments),
          operationType: operationTypes.CONTRIBUTION,
        };

        await store.dispatch(addOperation(operationData));
        setIsLoading(false);

        successSwal(t('ContributionAddedSuccessfully'));
      } catch (error) {
        errorSwal(t('ErrorAddingContribution'));
      } finally {
        handleCloseModal();
      }
    }
  };

  return (
    <ContributionModalContext.Provider
      value={{
        data: {
          date,
          totalAmount,
          comments,
          file,
          scriptureView,
          lastOperationDate,
          contributions: data.contributions,
          currentPage,
          rowsPerPage,
          isLoadingContributions,
          isLoading,
        },
        actions: {
          handleCloseModal,
          handleChangeDate,
          handleChangeTotalAmount,
          handleChangeComments,
          handleChangeFile,
          handleChangePage,
          handleChangeRowsPerPage,
          handleSaveContribution,
        },
      }}
    >
      {children}
    </ContributionModalContext.Provider>
  );
};

export const useContributionModal = () => {
  const context = useContext(ContributionModalContext);
  if (!context) {
    throw new Error(
      'useContributionModal must be used within a ContributionModalProvider'
    );
  }
  return context;
};
